import { useState } from 'react';
import type { AnalyzedPage, ProbeEntry, VisibilityState } from '../types';
import { engineName } from '../api';
import AnalyzedPages from './AnalyzedPages';
import AnswerBlock from './AnswerBlock';
import Info from './Glossary';

interface Brand { name: string; domain: string; aliases?: string[] }

const STATE_META: Record<VisibilityState, { label: string; color: string; note: string }> = {
  mentioned: { label: 'Mentioned in answer', color: '#6BE07A', note: 'AI names your brand directly in the answer text. The highest-value outcome.' },
  cited_only: { label: 'Used your page, didn’t name you', color: '#E0B341', note: 'Your page is among the sources, but the brand is not named in the answer.' },
  absent: { label: 'Absent', color: '#E0574A', note: 'AI does not mention you. It names competitors instead.' },
};

function competitorSearchUrl(name: string): string {
  return `https://www.google.com/search?q=${encodeURIComponent(name)}`;
}

function ProbeCard({ p, brandTokens, brandDomain }: { p: ProbeEntry; brandTokens: string[]; brandDomain: string }): JSX.Element {
  const meta = STATE_META[p.state];
  return (
    <div className="border-t border-warm-dark-2 py-4">
      <div className="flex items-start gap-2 mb-2">
        <p className="text-sm text-text-primary flex-1">“{p.query}”</p>
        <span
          className={`font-mono text-sm uppercase px-1.5 py-0.5 border rounded whitespace-nowrap ${
            p.origin === 'ai' ? 'text-cyan border-cyan/40' : 'text-text-secondary border-warm-dark-2'
          }`}
          title={p.origin === 'ai' ? 'Generated from your page content' : 'Your own question'}
        >
          {p.origin === 'ai' ? 'AI-generated' : 'your question'}
        </span>
      </div>
      <div className="flex items-center gap-2 mb-2">
        <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: meta.color }} />
        <span className="text-sm font-medium" style={{ color: meta.color }}>
          {meta.label}
        </span>
        <Info term={p.state} />
        <span className="font-mono text-sm text-text-secondary">{p.detail}</span>
      </div>
      <p className="text-sm text-text-secondary mb-2">{meta.note}</p>
      {p.competitors.length > 0 && (
        <div>
          <p className="font-mono text-sm uppercase tracking-widest text-text-secondary mb-1">
            Who AI names instead of you
          </p>
          <div className="flex flex-wrap gap-1.5">
            {p.competitors.map((c) => (
              <a
                key={c}
                href={competitorSearchUrl(c)}
                target="_blank"
                rel="noreferrer"
                className="font-mono text-sm px-2 py-0.5 rounded bg-warm-dark-2 text-text-primary hover:text-cyan transition"
              >
                {c} ↗
              </a>
            ))}
          </div>
        </div>
      )}
      <AnswerBlock answer={p.answer} citations={p.citations} brandTokens={brandTokens} brandDomain={brandDomain} />
    </div>
  );
}

export default function VisibilityPanel({
  probes,
  brand,
  analyzedPages = [],
  onAddQuestion,
  adding,
  onShowAbout,
}: {
  probes: ProbeEntry[];
  brand: Brand | null;
  analyzedPages?: AnalyzedPage[];
  onAddQuestion: (query: string) => void;
  adding: boolean;
  onShowAbout: () => void;
}): JSX.Element {
  const brandTokens = brand ? [brand.name, ...(brand.aliases ?? [])] : [];
  const brandDomain = brand?.domain ?? '';
  const [q, setQ] = useState('');
  const [copied, setCopied] = useState(false);
  const model = probes[0]?.model;
  const samples = probes[0]?.samples;

  const cited = probes.filter((p) => p.state !== 'absent').length;
  // Aggregate the competitors AI names most often across all questions.
  const freq = new Map<string, number>();
  for (const p of probes) for (const c of p.competitors) freq.set(c, (freq.get(c) ?? 0) + 1);
  const topCompetitors = [...freq.entries()].sort((a, b) => b[1] - a[1]).slice(0, 6).map(([c]) => c);

  function submit(): void {
    if (!q.trim() || adding) return;
    onAddQuestion(q.trim());
    setQ('');
  }

  function copyQuestions(): void {
    void navigator.clipboard.writeText(probes.map((p) => p.query).join('\n')).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }

  return (
    // max-w-5xl is 64rem, the width every other block on this page uses. At
    // max-w-2xl this one sat narrower than the results above and below it, so
    // the column visibly stepped in and out as you scrolled.
    <div className="w-full max-w-5xl mx-auto border border-cyan/30 rounded-xl p-5">
      <div className="flex items-baseline justify-between mb-1">
        <h2 className="font-serif text-lg">
          Visibility: <span className="text-cyan">the real result</span>
        </h2>
        <span className="font-mono text-sm text-text-secondary">
          direction B{model ? ` · ${engineName(model)}` : ''}{samples ? ` · ${samples}× samples` : ''}
        </span>
      </div>
      <p className="text-sm text-text-secondary mb-2">
        This actually asked AI (web search) whether it mentions you. Unlike the Readiness score, this is not a
        prediction. It is reality.
      </p>

      <AnalyzedPages pages={analyzedPages} />

      {/* Honest headline summary */}
      <div className="rounded-lg p-3 mb-2" style={{ background: cited > 0 ? 'rgba(0,255,212,0.06)' : 'rgba(224,87,74,0.06)' }}>
        <p className="text-sm">
          <span style={{ color: cited > 0 ? '#00FFD4' : '#E0574A' }} className="font-medium">
            AI names you in {cited} of {probes.length} answers.
          </span>{' '}
          {cited === 0 && (
            <span className="text-text-secondary">
              You have no AI visibility on these buyer questions yet.
              {topCompetitors.length > 0 && <> AI names these instead: {topCompetitors.join(', ')}.</>}
            </span>
          )}
        </p>
        {cited === 0 && (
          <p className="text-sm text-text-secondary mt-2">
            Don't be discouraged. This is normal for a young or niche brand, and it's a to-do list, not a verdict.
            Content is only ~a third of your odds; the rest is off-site reputation you build over time.{' '}
            <button onClick={onShowAbout} className="text-cyan hover:underline">
              See how AI citation really works →
            </button>{' '}
            And be sceptical of anyone charging a lot to “guarantee” mentions. The Fix it plan is yours, for free.
          </p>
        )}
      </div>

      {probes.map((p, i) => (
        <ProbeCard key={i} p={p} brandTokens={brandTokens} brandDomain={brandDomain} />
      ))}

      {/* Turn the questions into on-site content, the actionable next step */}
      <div className="border-t border-warm-dark-2 pt-3 mt-1">
        <div className="flex items-center justify-between gap-2 mb-1">
          <span className="font-mono text-sm uppercase tracking-widest text-text-secondary">
            Use these questions on your site
          </span>
          <button
            onClick={copyQuestions}
            className="font-mono text-sm border border-cyan/40 text-cyan rounded px-3 py-1 hover:bg-cyan/10 transition"
          >
            {copied ? 'copied ✓' : 'copy all'}
          </button>
        </div>
        <p className="text-sm text-text-secondary">
          These are the real questions buyers ask an AI where you should appear. Publish clear answers to them on your
          site (an FAQ / help section with <span className="font-mono">FAQPage</span> schema) so AI can retrieve and
          cite you. That's how you turn “absent” into “mentioned”. This tool diagnoses and recommends; it does not write
          the content for you.
        </p>
      </div>

      {/* Custom question, probe your own query */}
      <div className="border-t border-warm-dark-2 pt-3 mt-3">
        <label className="font-mono text-sm uppercase tracking-widest text-text-secondary">Ask your own question</label>
        <div className="flex gap-2 mt-1">
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && submit()}
            placeholder="e.g. best project management tool for small teams"
            className="flex-1 bg-warm-dark-2 border border-warm-dark-2 focus:border-cyan/60 rounded-lg px-3 py-2 text-text-primary placeholder:text-text-secondary outline-none text-sm"
          />
          <button
            onClick={submit}
            disabled={adding || !q.trim()}
            className="font-mono text-sm border border-cyan/40 text-cyan rounded px-4 disabled:opacity-50 hover:bg-cyan/10 transition"
          >
            {adding ? 'probing…' : 'Probe'}
          </button>
        </div>
      </div>

      <p className="font-mono text-sm text-text-secondary mt-3 border-t border-warm-dark-2 pt-3">
        ⓘ A snapshot in time, non-deterministic → aggregated over {samples ?? 'N'} runs. Not every model returns
        citations, so “cited only” can be measured only where the model exposes sources.
      </p>
    </div>
  );
}
